import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export function encode<S>(value: S): string {
  return btoa(JSON.stringify(value));
}
export function decode<S>(encoded: string): S {
  return JSON.parse(atob(encoded)) as S;
}

export function useQueryState<S>(
  key: string,
  initialState: S,
): [S, Dispatch<SetStateAction<S>>] {
  const [queryParams, history] = useOldQueryParams<Record<string, string>>();

  const state = useMemo(
    () => (queryParams[key] ? decode<S>(queryParams[key]) : initialState),
    [queryParams, key, initialState],
  );

  return [
    state,
    function set(newState) {
      const value =
        typeof newState === 'function'
          ? // @ts-expect-error - typescript should no it's a function
            (newState(state) as S)
          : newState;

      history.replace({ [key]: encode<S>(value) });
      return value;
    },
  ] as const;
}

type ChangeQueryParams<P extends {}> = {
  push: (params: Partial<P>) => void;
  replace: (params: Partial<P>) => void;
};

function useOldQueryParams<P extends {}>(): [P, ChangeQueryParams<P>] {
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);

  return [
    Object.fromEntries(searchParams.entries()) as P,
    {
      push(newParams) {
        const newSearchParams = new URLSearchParams(searchParams);

        for (const [k, v] of Object.entries(newParams)) {
          if (v === undefined) {
            newSearchParams.delete(k);
          } else {
            newSearchParams.set(k, String(v));
          }
        }

        history.push({ ...location, search: newSearchParams.toString() });
      },
      replace(newParams) {
        const newSearchParams = new URLSearchParams(searchParams);

        for (const [k, v] of Object.entries(newParams)) {
          if (v === undefined) {
            newSearchParams.delete(k);
          } else {
            newSearchParams.set(k, String(v));
          }
        }

        history.replace({ ...location, search: newSearchParams.toString() });
      },
    },
  ];
}
